import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Bandage, CreditCard, Clock, CheckCircle2, ArrowLeft } from 'lucide-react';

const Billing = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const tiers = [
    {
      id: 'express_1m',
      name: 'Express 1 mois',
      price: '1 200€',
      description: 'Démarrage rapide',
      features: ['4 publications', '~3 outlets A + 1 B', 'Support prioritaire', 'Rapport mensuel']
    },
    {
      id: 'discovery_3m',
      name: 'Discovery 3 mois',
      price: '4 500€',
      description: 'Exploration complète',
      features: ['15 publications', 'Mix A/B outlets', 'Stratégie personnalisée', 'Rapports mensuels']
    },
    {
      id: 'expert_6m',
      name: 'Expert 6 mois',
      price: '12 000€',
      description: 'Impact durable',
      features: ['40 publications', '1 Premium S inclus', 'Suivi SERP', 'Rapports bi-mensuels'],
      popular: true
    },
    {
      id: 'premium_1y',
      name: 'Premium 1 an',
      price: '30 000€',
      description: 'Couverture maximale',
      features: ['100 publications', '4 Premium S inclus', 'Dashboard temps réel', 'Support dédié']
    },
    {
      id: 'legacy_lifetime',
      name: 'Legacy Lifetime',
      price: '75 000€',
      description: 'Solution ultime',
      features: ['250 publications', '10 Premium S', 'Gestion complète', 'Account manager dédié']
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-2">
              <Bandage className="h-8 w-8 text-blue-600" />
              <span className="text-xl font-bold">AUTO-RÉPUTATION</span>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour Dashboard
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Facturation & Abonnements</h1>
          <p className="text-gray-600">Formule actuelle: <Badge variant="secondary">{user?.subscription_tier || 'Aucune'}</Badge></p>
        </div>

        {/* Notice "À venir" */}
        <Card className="mb-8 border-blue-500 bg-blue-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-blue-600" />
              <div>
                <h3 className="font-semibold text-blue-900">Intégration Stripe à venir</h3>
                <p className="text-sm text-blue-700">
                  Le paiement en ligne via Stripe sera disponible prochainement. 
                  Pour toute souscription immédiate, veuillez nous contacter directement.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pricing Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {tiers.map((tier) => (
            <Card 
              key={tier.id} 
              className={tier.popular ? 'border-blue-500 shadow-lg relative' : ''}
            >
              {tier.popular && (
                <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs px-3 py-1 rounded-bl-lg rounded-tr-lg">
                  Populaire
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-xl">{tier.name}</CardTitle>
                <CardDescription>{tier.description}</CardDescription>
                <div className="mt-4">
                  <span className="text-3xl font-bold text-gray-900">{tier.price}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {tier.features.map((feature) => (
                    <li key={`${tier.id}-${feature}`} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className="w-full mt-6" 
                  variant={tier.popular ? 'default' : 'outline'}
                  disabled
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  À venir
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact Info */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Besoin d&apos;aide ?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Pour toute question sur les formules ou pour une souscription immédiate, 
              contactez notre équipe commerciale.
            </p>
            <Button variant="outline">
              Nous contacter
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Billing;
